import { guards } from '@shared/helpers/typeguards.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import {
  AI_MODEL_USE_CASES,
  type AiModelUseCase,
} from './ai-model-use-cases.model';
import type { OpenAiReasoningEffort } from './open-ai-reasoning.model';

/**
 * Allowed Anthropic Claude models for Nodon.
 *
 * Kept in shared so both client and server can use the same allowlist.
 * Ids are the Anthropic Messages API aliases (not dated snapshots).
 */
export const CLAUDE_ALLOWED_MODELS = Object.freeze([
  'claude-opus-5',
  'claude-sonnet-5-5',
  'claude-sonnet-5',
  'claude-sonnet-4-6',
  'claude-haiku-4-5',
] as const);

const CLAUDE_ALLOWED_MODELS_SET: ReadonlySet<string> = new Set(
  CLAUDE_ALLOWED_MODELS,
);

export type ClaudeAllowedModels = (typeof CLAUDE_ALLOWED_MODELS)[number];

/**
 * Check if a value is an allowed Claude model.
 */
export const isAllowedClaudeModel = (
  model: unknown,
): model is ClaudeAllowedModels => {
  return guards.is.string(model) && CLAUDE_ALLOWED_MODELS_SET.has(model);
};

/**
 * Allowed xAI Grok models for Nodon.
 *
 * Kept in shared so both client and server can use the same allowlist.
 * Ids are the xAI API aliases (not dated snapshots).
 */
export const GROK_ALLOWED_MODELS = Object.freeze([
  'grok-4.7',
  'grok-4.6',
  'grok-4.5',
  'grok-4.3',
] as const);

const GROK_ALLOWED_MODELS_SET: ReadonlySet<string> = new Set(
  GROK_ALLOWED_MODELS,
);

export type GrokAllowedModels = (typeof GROK_ALLOWED_MODELS)[number];

/**
 * Check if a value is an allowed Grok model.
 */
export const isAllowedGrokModel = (
  model: unknown,
): model is GrokAllowedModels => {
  return guards.is.string(model) && GROK_ALLOWED_MODELS_SET.has(model);
};

/**
 * Allowed xAI Grok Imagine models for Nodon image generation.
 *
 * Separate from {@link GROK_ALLOWED_MODELS} so a text use case cannot default
 * to an Imagine model, and Generate image cannot pick a chat model.
 */
export const GROK_IMAGE_ALLOWED_MODELS = Object.freeze([
  'grok-imagine-image-2.0',
  'grok-imagine-image-quality',
  'grok-imagine-image',
] as const);

const GROK_IMAGE_ALLOWED_MODELS_SET: ReadonlySet<string> = new Set(
  GROK_IMAGE_ALLOWED_MODELS,
);

export type GrokImageAllowedModels = (typeof GROK_IMAGE_ALLOWED_MODELS)[number];

/**
 * Check if a value is an allowed Grok Imagine model.
 */
export const isAllowedGrokImageModel = (
  model: unknown,
): model is GrokImageAllowedModels => {
  return guards.is.string(model) && GROK_IMAGE_ALLOWED_MODELS_SET.has(model);
};

/**
 * Allowed OpenAI models for Nodon.
 *
 * Kept in shared so both client and server can use the same allowlist.
 */
export const OPEN_AI_ALLOWED_MODELS = Object.freeze([
  // Current frontier.
  'gpt-6-astra',
  // Near-Astra quality at a fifth of the Astra list price.
  'gpt-6.1-sol',
  // Previous frontier. The gpt-5.6 alias routes to gpt-5.6-sol.
  'gpt-5.6',
  // Previous frontier — no mini/nano variants ship for 5.5.
  'gpt-5.5',
  // Most efficient GPT-6 model, for high-volume tasks.
  'gpt-6-luna',
  // 5.4 family — the last generation with mini/nano variants.
  'gpt-5.4',
  'gpt-5.4-mini',
  'gpt-5.4-nano',
  // Previous frontier models — no mini/nano variants exist in the API.
  'gpt-5.2',
  'gpt-5.1',
] as const);

const OPEN_AI_ALLOWED_MODELS_SET: ReadonlySet<string> = new Set(
  OPEN_AI_ALLOWED_MODELS,
);

export type OpenAiAllowedModels = (typeof OPEN_AI_ALLOWED_MODELS)[number];

/**
 * Check if a value is an allowed OpenAI model.
 */
export const isAllowedOpenAiModel = (
  model: unknown,
): model is OpenAiAllowedModels => {
  return guards.is.string(model) && OPEN_AI_ALLOWED_MODELS_SET.has(model);
};

/**
 * Models that take `prompt_cache_options` and explicit `prompt_cache_breakpoint`s
 * (GPT-5.6 and later). They cache only at breakpoints and bill cache writes at
 * 1.25× input; earlier models cache matching prefixes automatically.
 */
const OPEN_AI_EXPLICIT_PROMPT_CACHE = {
  'gpt-6.1-sol': true,
  'gpt-6-astra': true,
  'gpt-6-luna': true,
  'gpt-5.6': true,
  'gpt-5.5': false,
  'gpt-5.4': false,
  'gpt-5.4-mini': false,
  'gpt-5.4-nano': false,
  'gpt-5.2': false,
  'gpt-5.1': false,
} as const satisfies Record<OpenAiAllowedModels, boolean>;

/** Whether {@link model} supports explicit prompt cache breakpoints. */
export const supportsOpenAiExplicitPromptCache = (
  model: OpenAiAllowedModels,
): boolean => OPEN_AI_EXPLICIT_PROMPT_CACHE[model];

/**
 * Responses API `detail` for PDF page images on an `input_file`. `auto` lets
 * the model decide: `high` on GPT-5.6 and later, `low` before. Extracted PDF
 * text is sent regardless.
 */
export const OPEN_AI_FILE_DETAILS = Object.freeze([
  'auto',
  'low',
  'high',
] as const);

export type OpenAiFileDetail = (typeof OPEN_AI_FILE_DETAILS)[number];

/**
 * Allowed OpenAI *image generation* models for Nodon.
 *
 * These are GPT Image models for the Responses API `image_generation` tool
 * (or the Images API), not mainline chat models.
 */
export const OPEN_AI_IMAGE_ALLOWED_MODELS = Object.freeze([
  // Fast everyday generation; quality comparable to gpt-image-2.
  'gpt-image-2.5-flare',
  // Higher quality and tighter edits; slower than Flare.
  'gpt-image-2.5-sunburst',
] as const);

const OPEN_AI_IMAGE_ALLOWED_MODELS_SET: ReadonlySet<string> = new Set(
  OPEN_AI_IMAGE_ALLOWED_MODELS,
);

export type OpenAiImageAllowedModels =
  (typeof OPEN_AI_IMAGE_ALLOWED_MODELS)[number];

/**
 * Check if a value is an allowed OpenAI image model.
 */
export const isAllowedOpenAiImageModel = (
  model: unknown,
): model is OpenAiImageAllowedModels => {
  return guards.is.string(model) && OPEN_AI_IMAGE_ALLOWED_MODELS_SET.has(model);
};

/**
 * Whether this use case selects a GPT Image model rather than a chat model.
 */
export const isOpenAiImageUseCase = (
  useCase: AiModelUseCase,
): useCase is Extract<
  AiModelUseCase,
  { provider: 'openai'; modality: 'image' }
> => useCase.provider === 'openai' && useCase.modality === 'image';

/**
 * Default OpenAI model used for the AI chat pipeline when the caller does
 * not provide a per-message override. Shared so the client-side model
 * picker can display what will actually be used when no selection has
 * been made, and the server pipeline can fall back consistently.
 */
export const DEFAULT_AI_CHAT_MODEL: OpenAiAllowedModels =
  AI_MODEL_USE_CASES.aiChat.defaultModel;

/**
 * Allowed Gemini *image generation* models for Nodon.
 *
 * NOTE: Our rendering endpoint always requests IMAGE output, so we only allow
 * models that support image generation via generateContent.
 */
export const GEMINI_IMAGE_ALLOWED_MODELS = Object.freeze([
  // Nano Banana 2 lineup. Ids verified against the Gemini API image models.
  // 2.1 updates gemini-3.1-flash-image at half the per-image price.
  'gemini-nano-banana-2.1',
  'gemini-3.1-flash-image',
  'gemini-3.1-flash-lite-image',
  'gemini-3-pro-image',
] as const);

const GEMINI_IMAGE_ALLOWED_MODELS_SET: ReadonlySet<string> = new Set(
  GEMINI_IMAGE_ALLOWED_MODELS,
);

export type GeminiImageAllowedModels =
  (typeof GEMINI_IMAGE_ALLOWED_MODELS)[number];

/**
 * Check if a value is an allowed Gemini image model.
 */
export const isAllowedGeminiImageModel = (
  model: unknown,
): model is GeminiImageAllowedModels => {
  return guards.is.string(model) && GEMINI_IMAGE_ALLOWED_MODELS_SET.has(model);
};

/**
 * Allowed Gemini *text* models for Nodon.
 *
 * NOTE: Gemini image models answer with pixels, so any use case that needs a
 * textual answer (e.g. geometry import, which returns JSON) must pick from
 * this list instead of {@link GEMINI_IMAGE_ALLOWED_MODELS}.
 */
export const GEMINI_TEXT_ALLOWED_MODELS = Object.freeze([
  // Stable text models (not *-image). Ids verified against the Gemini API lineup.
  'gemini-3.8-flash',
  'gemini-3.6-flash',
  'gemini-3.5-flash',
  'gemini-3.1-pro-preview',
] as const);

const GEMINI_TEXT_ALLOWED_MODELS_SET: ReadonlySet<string> = new Set(
  GEMINI_TEXT_ALLOWED_MODELS,
);

export type GeminiTextAllowedModels =
  (typeof GEMINI_TEXT_ALLOWED_MODELS)[number];

/**
 * Check if a value is an allowed Gemini text model.
 */
export const isAllowedGeminiTextModel = (
  model: unknown,
): model is GeminiTextAllowedModels => {
  return guards.is.string(model) && GEMINI_TEXT_ALLOWED_MODELS_SET.has(model);
};

/**
 * Whether a model is allowed for the allowlist of the use case's provider and
 * — when the use case has a {@link AiModelUseCase.modality} — that modality.
 */
export const isAllowedModelForUseCase = <T extends AiModelUseCase>(
  useCase: T,
  model: unknown,
): model is T['defaultModel'] => {
  switch (useCase.provider) {
    case 'openai':
      switch (useCase.modality) {
        case 'image':
          return isAllowedOpenAiImageModel(model);
        case 'text':
          return isAllowedOpenAiModel(model);
        default:
          return assertUnreachable(useCase);
      }
    case 'claude':
      return isAllowedClaudeModel(model);
    case 'grok':
      switch (useCase.modality) {
        case 'image':
          return isAllowedGrokImageModel(model);
        case 'text':
          return isAllowedGrokModel(model);
        default:
          return assertUnreachable(useCase);
      }
    case 'gemini':
      switch (useCase.modality) {
        case 'image':
          return isAllowedGeminiImageModel(model);
        case 'text':
          return isAllowedGeminiTextModel(model);
        default:
          return assertUnreachable(useCase);
      }
    default:
      return assertUnreachable(useCase);
  }
};

/**
 * Requested model when it is on the {@link AiModelUseCase} allowlist;
 * otherwise {@link AiModelUseCase.defaultModel}.
 * Empty or omitted {@link model} uses the default.
 */
export const resolveModelForUseCase = <T extends AiModelUseCase>(
  useCase: T,
  model: unknown,
): T['defaultModel'] => {
  if (isAllowedModelForUseCase(useCase, model)) {
    return model;
  }
  return useCase.defaultModel;
};

/** Where the active model for a use case came from. */
export type AiModelSource =
  /** No env override set; the registry default is used. */
  | 'default'
  /** A valid env override is set and used. */
  | 'env'
  /** An env override is set but not in the allowlist, so it is ignored and the default is used. */
  | 'env-ignored';

/**
 * A use case plus the model actually in effect at runtime, after applying the
 * env override and the provider allowlist. Shared so the admin endpoint and the
 * admin page agree on the shape.
 */
export type ResolvedAiModelUseCase = AiModelUseCase & {
  /** Raw env-override value at runtime, when the env var is set. */
  envValue?: string;
  /** Model actually used after override + allowlist resolution. */
  resolvedModel: string;
  /** Where {@link resolvedModel} came from. */
  source: AiModelSource;
  /** Whether the model can be picked per request in the UI. */
  uiSelectable: boolean;
  /**
   * `reasoning.effort` actually sent: the configured effort, adjusted to what
   * {@link resolvedModel} accepts. Undefined when none is configured.
   */
  resolvedReasoningEffort?: OpenAiReasoningEffort;
};

/**
 * Resolve the active model for a use case given its runtime env-override value.
 *
 * This is the single source of truth shared by the pipelines (via the server
 * wrapper) and the admin endpoint, so what runs and what the page shows can
 * never drift. An env override that is not in the provider allowlist is
 * reported as `env-ignored` and the default is used — matching pipeline
 * behaviour.
 */
export const resolveAiModelUseCase = (
  useCase: AiModelUseCase,
  envValue: string | undefined,
): ResolvedAiModelUseCase => {
  const base = { ...useCase, uiSelectable: useCase.uiSelectable ?? false };

  if (!useCase.envOverride || !envValue) {
    return { ...base, resolvedModel: useCase.defaultModel, source: 'default' };
  }

  if (isAllowedModelForUseCase(useCase, envValue)) {
    return { ...base, envValue, resolvedModel: envValue, source: 'env' };
  }

  return {
    ...base,
    envValue,
    resolvedModel: useCase.defaultModel,
    source: 'env-ignored',
  };
};
